import { computed, effect, inject, untracked } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { HttpResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  patchState, signalStore, withComputed, withHooks, withMethods, withState,
} from '@ngrx/signals';
import {
  AppError,
  FileSaveService,
  setError,
  setPending,
  setSuccess,
  SpinnerService,
  ToastType,
  withFiltersUpdate,
  withRequestHandler,
  withRequestStatus,
} from '@hglobal/common';
import {
  AdminFinancialRequest,
  AdminFinancialRequestAssigneeCategory,
  FinancialRequestStatus,
  UserFinancialRequest,
} from '@pt/models';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { GoogleAnalyticsService, ToastI18nService } from '@pt/services';
import { catchError, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { TextFormaterHelper } from '@pt/helpers';
import { Store } from '@ngrx/store';
import { RootState } from '@pt/store';
import { AuthSelectors } from '@pt/auth';

import { FINANCIAL_REQUESTS_API } from './api';
import {
  ChangeRequestStatusReq,
  CreateRequestReq,
  FinancialRequestsFilters,
  FinancialRequestsState,
  GetRequestDetailsReq,
  GetRequestsReq,
  GetUsersByIdsRes,
  SaveRequestEncryptedDataReq,
} from './models';

export const initialFinincialRequestsFilters: FinancialRequestsFilters = {
  search: null,
  statuses: null,
  status: null,
  categoryId: null,
  categoryIds: null,
  assigneeIds: null,
  locations: null,
  businessUnits: null,
  teams: null,
  services: null,
  functions: null,
  startDate: null,
  endDate: null,
  page: 1,
  limit: 12,
  sort: '',
};

const initialState: FinancialRequestsState = {
  filters: initialFinincialRequestsFilters,
  requestDetails: null,
  users: [],
  requests: [],
  locations: [],
  teams: [],
  services: [],
  functions: [],
  categories: [],
  assignees: [],
  businessUnits: [],
  itemsCount: 0,
  isAssignedToMe: false,
};

export const FinancialRequestsStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withRequestHandler(),
  withFiltersUpdate<FinancialRequestsFilters>(),
  withComputed(({ requests }) => (
    {
      tableRequests: computed(
        () => new MatTableDataSource<UserFinancialRequest | AdminFinancialRequest>(requests()),
      ),
    }
  )),
  withMethods(store => {
    const spinnerService = inject(SpinnerService);
    const apiService = inject(FINANCIAL_REQUESTS_API);
    const fileSaveService = inject(FileSaveService);

    const getRequestDetailsCallback = (params: GetRequestDetailsReq) => {
      return apiService.getRequestDetails(params).pipe(
        tapResponse({
          next: ({ request }) => {
            patchState(store, { requestDetails: request });
          },
          error: (error: AppError) => patchState(store, setError(error)),
        }),
        switchMap(response => apiService.getRequestDetailsEncryptedData(
          { requestIdHash: response.request.requestIdHash },
        )),
        tapResponse({
          next: res => {
            patchState(
              store,
              {
                requestDetails: {
                  ...store.requestDetails(), comments: res.financialMessages, files: res.files,
                },
              },
              setSuccess(),
            );
          },
          error: (error: AppError) => patchState(store, setError(error)),
        }),
        switchMap(({ financialMessages }) => apiService.getUsersByIds(
          { ids: financialMessages.map(item => +item.data.fromUser).join() },
        )),
        tapResponse({
          next: ({ users }) => {
            patchState(store, { users }, setSuccess());
          },
          error: (error: AppError) => patchState(store, setError(error)),
        }),
      );
    };

    return {
      getRequestIdHash: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(() => spinnerService.observe(apiService.getRequestIdHash(), true)),
          tapResponse({
            next: ({ request }) => {
              patchState(store, {
                requestDetails: {
                  ...store.requestDetails(), requestIdHash: request.requestIdHash,
                },
              }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      setAssignedToMe(value: boolean) {
        patchState(store, { isAssignedToMe: value });
      },
      getRequests: rxMethod<GetRequestsReq>(
        pipe(
          switchMap(params => spinnerService.observe(
            apiService.getRequests(Object.fromEntries(
              Object.entries(params).map(([key, value]) => [
                key,
                Array.isArray(value) ? value.join(',') : value,
              ]),
            ) as unknown as GetRequestsReq, store.isAssignedToMe()), true,
          )),
          tapResponse({
            next: ({ requests, itemsCount }) => {
              patchState(store, { requests, itemsCount }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      downloadReport: rxMethod<GetRequestsReq>(
        pipe(
          switchMap(params => apiService.downloadReport(Object.fromEntries(
            Object.entries(params).map(([key, value]) => [
              key,
              Array.isArray(value) ? value.join(',') : value,
            ]),
          ) as unknown as GetRequestsReq, store.isAssignedToMe())),
          tap((res: HttpResponse<Blob>) => {
            const contentDisposition = res.headers.get('content-disposition').trim();
            const [, filename] = contentDisposition.split('filename=');

            fileSaveService.save(res.body, JSON.parse(filename));
          }),
        ),
      ),
      getRequestDetails: rxMethod<GetRequestDetailsReq>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(params => getRequestDetailsCallback(params)),
        ),
      ),
      getRequestDetailsObservable(
        params: GetRequestDetailsReq,
      ): Observable<GetUsersByIdsRes> {
        patchState(store, setPending());

        return getRequestDetailsCallback(params);
      },
      getLocations: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getLocations(), true)),
          tapResponse({
            next: ({ locations }) => {
              patchState(store, { locations }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getTeams: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getTeams(), true)),
          tapResponse({
            next: ({ teams }) => {
              patchState(store, { teams }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getServices: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getServices(), true)),
          tapResponse({
            next: ({ services }) => {
              patchState(store, { services }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getBusinessUnits: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getBusinessUnits(), true)),
          tapResponse({
            next: ({ businessUnits }) => {
              patchState(store, { businessUnits }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getFunctions: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getFunctions(), true)),
          tapResponse({
            next: ({ functions }) => {
              patchState(store, { functions }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getFinancialCategories: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getFinancialCategories(), true)),
          tapResponse({
            next: ({ categories }) => {
              patchState(store, { categories }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
      getAssignees: rxMethod<void>(
        pipe(
          switchMap(() => spinnerService.observe(apiService.getAssignees(), true)),
          tapResponse({
            next: ({ users }) => {
              const assigneesSelectOptions = users.map(a => (
                { id: a.id, name: a.fullName }
              ));
              patchState(store, {
                assignees: assigneesSelectOptions,
              }, setSuccess());
            },
            error: (error: AppError) => patchState(store, setError(error)),
          }),
        ),
      ),
    };
  }),
  withMethods(store => {
    const toastService = inject(ToastI18nService);
    const googleAnalyticsService = inject(GoogleAnalyticsService);
    const storeRoot$ = inject(Store<RootState>);
    const spinnerService = inject(SpinnerService);
    const apiService = inject(FINANCIAL_REQUESTS_API);
    const currentUser = toSignal(storeRoot$.select(AuthSelectors.selectProfile));
    const saveRequestEncryptedDataCallback = (params: SaveRequestEncryptedDataReq) => {
      const {
        requestId,
        requestIdHash,
        message,
        status,
        files,
        source,
        action,
      } = params;
      return apiService.saveRequestEncryptedData({
        requestIdHash,
        data: {
          status,
          message,
        },
        files,
      }).pipe(tapResponse({
        next: () => {
          toastService.show(
            ToastType.Success,
            'financialRequests.toast.' + (
              action === 'create' ? 'create' : 'change'
            ),
            'financialRequests.toast.title',
            { iconName: 'success-round' },
          );

          if (source && source === 'dialog') {
            store.getRequestDetails({ requestId });
          } else {
            store.getRequests(store.filters());
          }
        },
        error: (error: AppError) => patchState(store, setError(error)),
      }));
    };

    return {
      createRequest: rxMethod<CreateRequestReq>(
        pipe(
          switchMap(params => spinnerService.observe(apiService.createRequest(params), true)
            .pipe(
              tapResponse({
                next: () => patchState(store, setPending()),
                error: (error: AppError) => patchState(store, setError(error)),
              }),
              switchMap(response => saveRequestEncryptedDataCallback({
                requestId: (
                  <UserFinancialRequest>response.request
                ).requestId,
                requestIdHash: store.requestDetails().requestIdHash,
                message: params.text,
                status: FinancialRequestStatus.New,
                files: params.files,
                action: 'create',
              })),
              tap({
                next: () => {
                  googleAnalyticsService.pushCustomEvent('personal_space_requests', 'new_request', {
                    id: currentUser().id,
                    element_name: 'success',
                    status: 'success',
                    category: TextFormaterHelper.transformToSnakeCase(store.categories()
                      .find(category => category.id === +params.categoryId)?.name),
                  });
                },
              }),
              catchError(error => {
                googleAnalyticsService.pushCustomEvent('personal_space_requests', 'new_request', {
                  id: currentUser().id,
                  element_name: 'error',
                  status: `${error.code}: ${error.message}`,
                  category: TextFormaterHelper.transformToSnakeCase(store.categories()
                    .find(category => category.id === +params.categoryId)?.name),
                });
                return of(null);
              }),
            )),
        ),
      ),

      updateRequestAssigneeCategory: rxMethod<
        {
          id: number;
          payload: AdminFinancialRequestAssigneeCategory;
          isListUpdated: boolean;
        }>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(data => apiService.patchRequestAssigneeCategory(data.id, data.payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setSuccess());
                store.updateFilters(store.filters());
                if (!data.isListUpdated) {
                  store.getRequestDetails({ requestId: data.id });
                }
              },
              error: (error: AppError) => patchState(store, setError(error)),
            }),
          )),
        ),
      ),
      changeRequestStatus: rxMethod<ChangeRequestStatusReq>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(params => spinnerService.observe(apiService.changeRequestStatus(params), true)
            .pipe(
              tapResponse({
                next: data => {
                  if (params.source === 'dialog') {
                    store.getRequestDetails({
                      requestId: 'request' in data ? data.request.requestId : data.id,
                    });
                  } else {
                    store.getRequests(store.filters());
                  }
                },
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            )),
        ),
      ),
      saveRequestEncryptedData: rxMethod<SaveRequestEncryptedDataReq>(
        pipe(
          switchMap(params => saveRequestEncryptedDataCallback(params)),
        ),
      ),
    };
  }),
  withHooks({
    onInit(store) {
      store.getLocations();
      store.getFinancialCategories();
      store.getTeams();
      store.getFunctions();
      store.getServices();
      store.getBusinessUnits();
      store.getRequests(store.filters);
      effect(() => {
        if (store.error()) {
          untracked(() => store.handleError(store.error()));
        }
      });
    },
  }),
);
